/**
 * Frontend view of the shared SCM platform catalog (plan 494, S-4).
 *
 * The catalog in `@codemerlin/shared` is data only; this module owns the pieces that need the
 * browser: icon components per `iconKey`, the build-time feature flags, and the fallback for a
 * platform value the catalog does not know (never shown as GitHub).
 */
import { FolderGit2, GitBranch, Github, Gitlab, Infinity as InfinityIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  SCM_PLATFORM_CATALOG,
  isScmPlatform,
  type ScmIconKey,
  type ScmPlatform,
  type ScmPlatformFeatureFlag,
} from '@codemerlin/shared';
import { isAzureDevOpsEnabled, isGiteaEnabled, isGitLabEnabled } from '@/config/features';

/** A platform value from the API: a catalog id, or an unknown string kept as it came. */
export type ScmPlatformValue = ScmPlatform | (string & Record<never, never>);

export const SCM_ICON_COMPONENTS: Record<ScmIconKey, LucideIcon> = {
  github: Github,
  gitlab: Gitlab,
  'git-branch': GitBranch,
  infinity: InfinityIcon,
};

/**
 * The same icons by lucide export name, for string-keyed icon lookups (the search palette resolves
 * `Icons[item.icon]`).
 */
export const SCM_ICON_NAMES: Record<ScmIconKey, string> = {
  github: 'Github',
  gitlab: 'Gitlab',
  'git-branch': 'GitBranch',
  infinity: 'Infinity',
};

/** Neutral repository icon for a platform the catalog does not know. */
const UNKNOWN_PLATFORM_ICON: LucideIcon = FolderGit2;

/**
 * Display name for a platform value.
 * - catalog id → its label;
 * - unknown string → the value itself (tells the user what it is; never "GitHub");
 * - missing → "GitHub", the platform of rows created before multi-SCM support.
 */
export function scmPlatformLabel(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '')
    return SCM_PLATFORM_CATALOG.github.label;
  return isScmPlatform(value) ? SCM_PLATFORM_CATALOG[value].label : value;
}

export function ScmPlatformIcon({
  platform,
  className,
}: {
  platform: ScmPlatformValue;
  className?: string;
}) {
  const Icon = isScmPlatform(platform)
    ? SCM_ICON_COMPONENTS[SCM_PLATFORM_CATALOG[platform].iconKey]
    : UNKNOWN_PLATFORM_ICON;
  return <Icon className={className} />;
}

/** Read on each call (live bindings), so tests can switch flags per case. */
const FEATURE_FLAGS: Record<ScmPlatformFeatureFlag, () => boolean> = {
  FEATURE_GITLAB_ENABLED: () => isGitLabEnabled,
  FEATURE_GITEA_ENABLED: () => isGiteaEnabled,
  FEATURE_AZURE_DEVOPS_ENABLED: () => isAzureDevOpsEnabled,
};

/**
 * Build-time flag gate only: false when the platform's feature flag is off, true otherwise.
 * Availability (e.g. Azure DevOps "coming soon") is a separate descriptor field.
 */
export function isScmPlatformFlagOn(platform: ScmPlatform): boolean {
  const flag = SCM_PLATFORM_CATALOG[platform].featureFlag;
  return flag === undefined || FEATURE_FLAGS[flag]();
}

/** "Coming soon": by availability, or a `comingSoonWhenFlagOff` platform whose flag is off. */
export function isScmPlatformComingSoon(platform: ScmPlatform): boolean {
  const descriptor = SCM_PLATFORM_CATALOG[platform];
  if (descriptor.availability === 'coming_soon') return true;
  return descriptor.comingSoonWhenFlagOff === true && !isScmPlatformFlagOn(platform);
}

/** Connect pages list a platform when its flag is on, or as "coming soon" when it asks to be. */
export function isScmPlatformListed(platform: ScmPlatform): boolean {
  return (
    isScmPlatformFlagOn(platform) || SCM_PLATFORM_CATALOG[platform].comingSoonWhenFlagOff === true
  );
}
